/**
 * RoyaleLeaderboardStrip
 *
 * The "Puzzle Race — Live Strip" leaderboard, ported from the authoritative
 * design (docs/design/project/Puzzle Race - Live Strip.html) to RN-for-Web.
 *
 * Physics (from royaleConstants, matching the design):
 *   - Rows are absolutely positioned and reorder by rank via an animated
 *     `top` transition (web CSS transition on the row's top).
 *   - Each player's piece sits at x% along the track as a function of THEIR
 *     OWN points only (MIN_X + points*step, capped at LEADER_X_POST). A player
 *     advances horizontally ONLY when they personally score — another player
 *     scoring never shifts your piece (item 3). Vertical row order still
 *     reflects live rank.
 *   - Piece glyph = tier from points (PIECE_BANDS); the current leader's
 *     piece becomes a KING (leader badge) once KING_ENABLED_AT is reached.
 *   - "You" row is teal-highlighted. When there are >LEADERBOARD_VISIBLE
 *     players, we show the top-N plus a pinned "you" row if you're outside it.
 *
 * Pure presentational: consumes the store-derived players + leaderboard.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';

import {
  KING_ENABLED_AT,
  LEADERBOARD_VISIBLE,
  LEADER_X_POST,
  MIN_X,
} from '@/Screens/FunChallenges/royaleConstants';
import type {
  PieceTier,
  PlayerRoyaleState,
} from '@/Screens/FunChallenges/royaleTypes';
import {buildColorMap} from '@/Screens/FunChallenges/util/royaleColors';

const TEAL_DARK = '#1F6C63';
const BROWN = '#4D3F37';
const CREAM3 = '#EDE4CC';
const TEAL_BG = 'rgba(61,171,158,0.08)';
const TEAL_BORDER = 'rgba(61,171,158,0.25)';

// Row height tuned so the full top-10 (+ a pinned "you" row) fits in the rail
// without forcing a scroll at common viewport heights (task 2).
const ROW_H = 46;

// Bare coloured glyphs (no circle), per design.
const GLYPHS: Record<PieceTier | 'king', string> = {
  pawn: '♟',
  knight: '♞',
  bishop: '♝',
  rook: '♜',
  queen: '♛',
  king: '♚',
};

// Two-phase race physics (per the user):
//   Phase 1 — until the LEADER's own-points position reaches PHASE1_CROSSOVER_X
//     (75%): each player advances by THEIR OWN points only (MIN_X + pts*step).
//     A player moves only when they personally score; nobody is pushed by
//     others.
//   Phase 2 — once the leader crosses 75%: switch to LEADER-RELATIVE — the
//     leader anchors at LEADER_X_POST (front) and everyone else is placed by
//     their points-gap behind the leader, so the field spreads/compresses
//     dynamically as the leader pulls away or trailers close in.
const X_STEP_PER_PT = 5; // Phase-1 % of track gained per own point.
const PHASE1_CROSSOVER_X = 75; // Leader crossing this (%) flips to Phase 2.

/** Phase-1 own-points position (no leader influence). */
function ownX(score: number): number {
  if (score <= 0) {
    return MIN_X;
  }
  return Math.min(PHASE1_CROSSOVER_X, MIN_X + score * X_STEP_PER_PT);
}

/** Has the leader's own-points position reached the 75% crossover? */
function isPhase2(leaderScore: number): boolean {
  return ownX(leaderScore) >= PHASE1_CROSSOVER_X;
}

/**
 * Track position for a player.
 *  - Phase 1 (leader < 75%): own-points mapping (ownX).
 *  - Phase 2 (leader ≥ 75%): leader-relative — leader at LEADER_X_POST, others
 *    behind by their points-gap scaled across (LEADER_X_POST → MIN_X).
 */
function calcX(score: number, leaderScore: number): number {
  if (leaderScore <= 0) {
    return MIN_X;
  }
  if (!isPhase2(leaderScore)) {
    return ownX(score);
  }
  const avail = LEADER_X_POST - MIN_X;
  const scale = avail / leaderScore; // % per point of gap behind the leader
  const x = LEADER_X_POST - (leaderScore - score) * scale;
  return Math.max(MIN_X, Math.min(LEADER_X_POST, x));
}

interface Props {
  /** Store players map. */
  players: Record<string, PlayerRoyaleState>;
  /** Store leaderboard order (winner first). */
  leaderboard: string[];
  /** Player ids in JOIN ORDER — for join-order colour assignment. */
  joinOrder: string[];
  myUserId: string;
  /** Spectator mode: rows are tappable to follow that player (#5). */
  selectable?: boolean;
  /** Currently-followed player id (spectator mode) — highlighted. */
  spectating?: string | null;
  /** Follow a player (spectator mode). */
  onFollow?: (userId: string | null) => void;
  /** Stretch the card to fill the column height (race track to bottom, #3). */
  fill?: boolean;
}

export default function RoyaleLeaderboardStrip({
  players,
  leaderboard,
  joinOrder,
  myUserId,
  selectable = false,
  spectating = null,
  onFollow,
  fill = false,
}: Props) {
  // Colour by join order, with "you" forced to green (config: DIFFERENT_COLOR).
  const colorOf = React.useMemo(
    () => buildColorMap(joinOrder, myUserId),
    [joinOrder, myUserId],
  );
  const ranked = leaderboard.map(id => players[id]).filter(Boolean);
  const leaderScore = ranked[0]?.points ?? 0;
  const kingId =
    ranked[0] && ranked[0].points >= KING_ENABLED_AT ? ranked[0].userId : null;

  // Decide which rows to render. ≤ visible → all. Otherwise top-N, plus a
  // pinned "you" row if you fall outside the top-N.
  const myRank = ranked.findIndex(p => p.userId === myUserId);
  const showAll = ranked.length <= LEADERBOARD_VISIBLE;
  const topRows = showAll ? ranked : ranked.slice(0, LEADERBOARD_VISIBLE);
  const pinnedYou =
    !showAll && myRank >= LEADERBOARD_VISIBLE ? ranked[myRank] : null;

  // Visual rows in display order; the pinned "you" row appears as an extra
  // bottom slot (rank label keeps your true rank).
  const rows = pinnedYou ? [...topRows, pinnedYou] : topRows;
  const trackHeight = rows.length * ROW_H + 16;

  return (
    <View style={[styles.root, fill && styles.rootFill]}>
      <View style={styles.header}>
        <CCText style={styles.headerCell}>Player</CCText>
        <CCText style={[styles.headerCell, styles.headerTrack]}>
          Race track
        </CCText>
        <CCText style={[styles.headerCell, styles.headerPts]}>Pts</CCText>
      </View>

      <View style={[styles.lanes, {height: trackHeight}]}>
        {rows.map((player, displayIdx) => {
          const trueRank = ranked.findIndex(p => p.userId === player.userId);
          const isYou = player.userId === myUserId;
          const isKing = player.userId === kingId;
          const tier: PieceTier | 'king' = isKing ? 'king' : player.piece;
          const x = calcX(player.points, leaderScore);
          const top = displayIdx * ROW_H + 8;
          const isFollowed = selectable && spectating === player.userId;
          const RowTag: any = selectable ? Pressable : View;

          return (
            <RowTag
              key={player.userId}
              onPress={
                selectable && onFollow
                  ? () => onFollow(player.userId)
                  : undefined
              }
              // RN-for-Web honours transition via style; absolute top reorders.

              style={[
                styles.row,
                {top, transition: 'top 0.55s cubic-bezier(0.4,0,0.2,1)'} as any,
              ]}>
              {(isYou || isFollowed) && (
                <View style={styles.youHighlight} pointerEvents="none" />
              )}

              {/* rank + dot + name */}
              <View style={styles.nameCol}>
                <MedalDot rank={trueRank} />
                <View
                  style={[
                    styles.colorDot,
                    {backgroundColor: colorOf(player.userId)},
                  ]}
                />
                <CCText
                  style={[styles.name, isYou && styles.nameYou]}
                  numberOfLines={1}>
                  {isYou ? 'You' : player.name || 'Player'}
                </CCText>
                {player.eliminated && (
                  <View style={styles.outBadge}>
                    <CCText style={styles.outBadgeText}>OUT</CCText>
                  </View>
                )}
              </View>

              {/* track */}
              <View style={styles.track}>
                <View style={styles.trackLine} />
                <View
                  style={[
                    styles.piece,
                    {
                      left: `${x}%`,
                      transform: [{translateX: -13}],
                      transition: 'left 0.5s cubic-bezier(0.25,0.46,0.45,0.94)',
                    } as any,
                  ]}>
                  <CCText
                    style={[
                      styles.pieceGlyph,
                      {color: colorOf(player.userId)},
                    ]}>
                    {GLYPHS[tier]}
                  </CCText>
                </View>
              </View>

              {/* score */}
              <View style={styles.ptsCol}>
                <CCText style={[styles.pts, isYou && styles.ptsYou]}>
                  {player.points}
                </CCText>
              </View>
            </RowTag>
          );
        })}
      </View>
    </View>
  );
}

function MedalDot({rank}: {rank: number}) {
  const medal = ['#D1AB41', '#A0A0A0', '#B87333'];
  if (rank >= 0 && rank < 3) {
    return (
      <View style={[styles.medal, {backgroundColor: medal[rank]}]}>
        <CCText style={styles.medalText}>{rank + 1}</CCText>
      </View>
    );
  }
  return <CCText style={styles.rankText}>#{rank + 1}</CCText>;
}

const styles = StyleSheet.create({
  root: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: CREAM3,
    overflow: 'hidden',
  },
  // Fill the column height so the race track extends to the bottom (#3).
  rootFill: {flex: 1},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: CREAM3,
    backgroundColor: '#FAF6EB',
  },
  headerCell: {
    width: 174,
    fontSize: 10,
    fontWeight: '700',
    color: 'rgba(77,63,55,0.4)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  headerTrack: {flex: 1, width: undefined, textAlign: 'center'},
  headerPts: {width: 68, textAlign: 'right'},
  lanes: {position: 'relative'},
  row: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: ROW_H,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  youHighlight: {
    position: 'absolute',
    top: 3,
    bottom: 3,
    left: 3,
    right: 3,
    borderRadius: 10,
    backgroundColor: TEAL_BG,
    borderWidth: 1.5,
    borderColor: TEAL_BORDER,
  },
  nameCol: {
    width: 174,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
  },
  medal: {
    width: 20,
    height: 20,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  medalText: {fontSize: 10, fontWeight: '800', color: '#fff'},
  rankText: {
    width: 20,
    fontSize: 11,
    fontWeight: '600',
    color: 'rgba(77,63,55,0.35)',
    textAlign: 'right',
  },
  colorDot: {width: 8, height: 8, borderRadius: 999},
  name: {
    flex: 1,
    fontSize: 13.5,
    fontWeight: '600',
    color: BROWN,
  },
  nameYou: {color: TEAL_DARK, fontWeight: '800'},
  outBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 5,
    backgroundColor: 'rgba(205,44,57,0.12)',
  },
  outBadgeText: {fontSize: 9, fontWeight: '800', color: '#CD2C39'},
  track: {flex: 1, height: ROW_H, justifyContent: 'center'},
  trackLine: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: 'rgba(77,63,55,0.07)',
  },
  // Full-height absolute column so the glyph centers vertically on the track
  // line (was top-anchored → glyph hung high/off-centre in the 50px lane).
  piece: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pieceGlyph: {fontSize: 26, lineHeight: 28, textAlign: 'center'},
  ptsCol: {width: 68, alignItems: 'flex-end', paddingRight: 14},
  pts: {
    fontSize: 20,
    fontWeight: '800',
    color: BROWN,
  },
  ptsYou: {color: TEAL_DARK},
});
